// imports
import React from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Field, FormikProvider } from 'formik';
// components
import styles from './Consent.module.css';
import { Background } from 'layout';
import { AuthenticateLogo, Button, Image, Loader, ValidatedRecaptcha } from 'ui';
import { setButtonVariant } from 'utils/utilities';
// assets
import { ReactComponent as AuthenticateRgb } from 'assets/images/authenticate_logo_rgb.svg';
import { MEDALLION_LINK_ERR_MSG } from 'utils/constants';
import useConsentForm from './useConsentForm';

function Consent({ renderNextStep, isLastPoint }) {
    const {
        formik,
        captchaRef,
        profile,
        reauthenticateRoute,
        disabledCaptcha,
        dashboardTitle,
        dashboardDesc,
        themeButton,
    } = useConsentForm({ renderNextStep, isLastPoint });

    const { values, isSubmitting, isValid, dirty, handleChange, handleSubmit } = formik;

    // Load component =  if Medallion session -> is profile loading | Reauthenticate session -> don't require profile to load
    const isLoading = reauthenticateRoute
        ? false
        : isSubmitting || profile.loading || (!profile?.company?.uuid && !profile?.error);
    const isWrapperLoading = reauthenticateRoute
        ? true
        : !profile.loading && profile?.company?.uuid;

    if (isLoading) {
        return (
            <Background className={styles.verifyWelcome}>
                <Loader />
            </Background>
        );
    }

    const redirectScreen =
        profile?.error === MEDALLION_LINK_ERR_MSG.EXPIRED
            ? '/session-expired?session=expired'
            : '/session-expired?session=invalid';

    return !profile.error ? (
        <Background className={styles.verifyWelcome}>
            {isWrapperLoading && (
                <Card.Header bsPrefix={styles.cardHeader}>
                    <p className={styles.heading}>{dashboardTitle}</p>
                </Card.Header>
            )}
            <FormikProvider value={formik}>
                <form onSubmit={handleSubmit}>
                    <div className={styles.body}>
                        <Card.Body>
                            <div>
                                <Container>
                                    <Row className={styles.row}>
                                        <Col xs={1} className={styles.iconCol}>
                                            <Image name="identity-gray.svg" />
                                        </Col>
                                        <Col xs={11} className={styles.content}>
                                            {dashboardDesc}
                                        </Col>
                                    </Row>
                                    <Row className={styles.row}>
                                        <Col xs={1} className={styles.iconCol}>
                                            <Image name="secure-shield-gray.svg" />
                                        </Col>
                                        <Col xs={11} className={styles.content}>
                                            We use machine learning, computer vision, and AI for a
                                            forensic identity authentication.
                                        </Col>
                                    </Row>
                                    <Row className={styles.row}>
                                        <Col xs={1} className={styles.iconCol}>
                                            <Image name="digital-lock-gray.svg" />
                                        </Col>
                                        <Col xs={11} className={styles.content}>
                                            Your information will only be used by{' '}
                                            <a
                                                href="https://authenticating.com/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.link}
                                            >
                                                Authenticate.com
                                            </a>{' '}
                                            for verification purposes.
                                        </Col>
                                    </Row>
                                    <Row className={styles.row}>
                                        <Col xs={1} className={styles.iconCol}>
                                            <Image name="quick-process-gray.svg" />
                                        </Col>
                                        <Col xs={11} className={styles.content}>
                                            This process takes only a couple of minutes.
                                        </Col>
                                    </Row>
                                </Container>

                                {!reauthenticateRoute && (
                                    <Form.Check
                                        className={styles.formCheck}
                                        id="agree"
                                        name="agree"
                                        type="checkbox"
                                        custom
                                        onChange={handleChange}
                                        checked={values.agree}
                                        label={
                                            <p className={styles.checkLabel}>
                                                I agree to{' '}
                                                <a
                                                    href="https://authenticate.com"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.link}
                                                >
                                                    Authenticating.com
                                                </a>
                                                's{' '}
                                                <a
                                                    href="https://authenticate.com/terms-of-use"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.link}
                                                >
                                                    Terms of Use
                                                </a>{' '}
                                                and{' '}
                                                <a
                                                    href="https://authenticate.com/privacy-policy"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.link}
                                                >
                                                    Privacy Policy
                                                </a>
                                                , consent to the collection of my personal data and
                                                facial biometrics (if applicable) for identity
                                                verification purposes and acknowledge the{' '}
                                                <a
                                                    href="https://authenticate.com/biometric-policy"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.link}
                                                >
                                                    data retention policy
                                                </a>
                                                .
                                            </p>
                                        }
                                    />
                                )}

                                {!disabledCaptcha && (
                                    <Field
                                        render={(fieldProps) => (
                                            <ValidatedRecaptcha {...fieldProps} ref={captchaRef} />
                                        )}
                                        name="captcha"
                                    />
                                )}
                            </div>
                        </Card.Body>
                        {!profile.loading && (
                            <>
                                <Card.Footer bsPrefix={styles.cardFooter}>
                                    <Button
                                        disabled={!isValid || !dirty}
                                        block
                                        type="submit"
                                        className={styles.startBtn}
                                        variant={setButtonVariant(themeButton)}
                                    >
                                        {reauthenticateRoute ? (
                                            'ReAuthenticate™'
                                        ) : (
                                            <AuthenticateLogo />
                                        )}
                                    </Button>
                                </Card.Footer>

                                {reauthenticateRoute && (
                                    <div className={styles.brandingLogo}>
                                        POWERED BY <AuthenticateRgb />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </form>
            </FormikProvider>
        </Background>
    ) : (
        <Navigate to={redirectScreen} replace />
    );
}

Consent.propTypes = {
    renderNextStep: PropTypes.func,
    isLastPoint: PropTypes.any,
};

export default Consent;
